import { useEffect, useRef } from "react";
import { formatMoney } from "@/shared/format";
import type { PortalError, PortalPreviewOrder } from "../portalOrdersApi";
import { CostoNonNotoBox } from "./CostoNonNotoBox";

const TITLE_ID = "portal-order-confirm-title";

interface PortalOrderConfirmDialogProps {
  /** Etichetta del fornitore, es. "ITYHOME". */
  supplierLabel: string;
  previews: PortalPreviewOrder[];
  /** Ordini che il backend ha già escluso (SKU mancante, ordine non trovato). */
  previewErrors: PortalError[];
  sending: boolean;
  sendError: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Conferma dell'ordine automatico sul portale del fornitore.
 *
 * Modale di proposito: oltre questo bottone il fornitore riceve ordini veri e
 * parte un impegno di spesa. È l'unico punto in cui l'operatore vede COSA
 * verrà comprato e A QUANTO prima che succeda.
 *
 * Stessa ricetta modale degli altri dialog di ordini (role=dialog, overlay,
 * Esc, click-fuori, focus iniziale su Annulla, footer Annulla/Conferma):
 * ``CarrierSwitchConfirmDialog``, ``PackagesConfirmDialog``.
 *
 * Il numero che conta davvero qui è il TOTALE DA BONIFICARE — è con quello
 * che Davide fa il bonifico subito dopo — e sta nel footer, accanto al
 * bottone di conferma, non sepolto in fondo alla lista.
 */
export function PortalOrderConfirmDialog({
  supplierLabel,
  previews,
  previewErrors,
  sending,
  sendError,
  onConfirm,
  onClose,
}: PortalOrderConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Esc non deve poter interrompere un invio già partito: gli ordini
      // stanno venendo creati uno per uno sul portale.
      if (event.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  const ordinabili = previews.filter((o) => !o.already_ordered);
  const gia = previews.filter((o) => o.already_ordered);
  const totale = ordinabili.reduce((acc, o) => acc + o.total_gross, 0);
  const n = ordinabili.length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !sending) onClose();
      }}
    >
      <div className="flex max-h-[86vh] w-full max-w-2xl flex-col rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-serif text-[17px] font-medium text-ink">
            Ordina a {supplierLabel}
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-3">
            Alla conferma {n === 1 ? "parte 1 ordine vero" : `partono ${n} ordini veri`} sul portale{" "}
            {supplierLabel}, con la nostra reference nel campo Nota. Pagamento con bonifico, ritiro
            in sede.
          </p>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {previewErrors.length > 0 && (
            <div role="alert" className="mb-3 rounded-sm border border-line bg-paper px-3 py-2">
              {previewErrors.map((err) => (
                <p key={`${err.order_id}-${err.message}`} className="text-[11.5px] text-red">
                  Ordine {err.order_id}: {err.message} — escluso dall'ordine
                </p>
              ))}
            </div>
          )}

          {gia.length > 0 && (
            <div role="alert" className="mb-3 rounded-sm border border-line bg-paper px-3 py-2">
              <p className="text-[11.5px] text-ink-2">
                {gia.length === 1 ? "1 ordine risulta" : `${gia.length} ordini risultano`} già
                ordinat{gia.length === 1 ? "o" : "i"} a {supplierLabel}: non {""}
                {gia.length === 1 ? "verrà rimandato" : "verranno rimandati"}.{" "}
                <span className="font-mono">{gia.map((o) => o.reference).join(", ")}</span>
              </p>
            </div>
          )}

          {ordinabili.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-ink-3">
              Non resta nessun ordine da mandare a {supplierLabel}.
            </p>
          ) : (
            <ul className="space-y-4">
              {ordinabili.map((ordine) => (
                <li key={ordine.reference} data-testid={`portal-order-${ordine.reference}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-3">
                    <span className="font-mono text-[12.5px] font-medium text-ink">
                      {ordine.reference}
                    </span>
                    {/* Netto E lordo, entrambi: le righe qui sotto sono IVA
                        esclusa (è così che il portale espone i prezzi), ma si
                        bonifica il lordo. Mostrarne uno solo lascia il dubbio
                        su quale sia dei due. */}
                    <span className="text-[12px] tabular-nums text-ink-2">
                      {formatMoney(ordine.total_net)}{" "}
                      <span className="text-[11px] text-ink-3">+ IVA =</span>{" "}
                      <span className="font-medium text-ink">
                        {formatMoney(ordine.total_gross)}
                      </span>
                    </span>
                  </div>
                  <CostoNonNotoBox riferimento={ordine.reference} />
                  <table className="w-full text-[12px]">
                    <tbody>
                      {ordine.items.map((item) => (
                        <tr key={`${ordine.reference}-${item.sku}`} className="align-baseline">
                          <td className="py-0.5 pr-2 font-mono text-[11.5px] text-ink-2 whitespace-nowrap">
                            {item.sku}
                          </td>
                          <td className="py-0.5 pr-2 text-ink-2">{item.title}</td>
                          <td className="py-0.5 pr-2 tabular-nums text-ink-3 whitespace-nowrap">
                            ×{item.quantity}
                          </td>
                          <td className="py-0.5 pr-2 tabular-nums text-ink-3 whitespace-nowrap">
                            {formatMoney(item.unit_price)}
                          </td>
                          <td className="py-0.5 text-right tabular-nums text-ink whitespace-nowrap">
                            {formatMoney(item.line_total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {ordine.warnings.map((w) => (
                    <p key={w} role="alert" className="mt-1 text-[11px] text-red">
                      {w}
                    </p>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="border-t border-line px-5 py-3">
          {sendError && (
            <p role="alert" className="mb-2 text-[11.5px] text-red">
              {sendError}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            {/* Il totale del bonifico: è il motivo per cui questo popup esiste,
                quindi vive accanto al bottone che lo fa partire. */}
            <div>
              <p className="text-[11px] text-ink-3">Da bonificare (IVA inclusa)</p>
              <p
                data-testid="portal-order-total"
                className="font-serif text-[19px] tabular-nums text-ink"
              >
                {formatMoney(totale)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                ref={cancelRef}
                type="button"
                data-testid="portal-order-cancel-btn"
                disabled={sending}
                onClick={onClose}
                className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-55"
              >
                Annulla
              </button>
              <button
                type="button"
                data-testid="portal-order-confirm-btn"
                disabled={sending || n === 0}
                aria-busy={sending}
                onClick={onConfirm}
                className="rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-55"
              >
                {sending
                  ? "Sto ordinando…"
                  : `Ordina ${n} ordin${n === 1 ? "e" : "i"} — ${formatMoney(totale)}`}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
